// Home v2 — "asset types" dropdown. Switches the holdings list between Tokens /
// NFTs / DeFi, driving the same `openTab` model the previous dashboard used
// (tokens / collectibles / defi). Closes on outside press.
import React, { useCallback, useRef, useState } from 'react'
import { Platform, Pressable, View } from 'react-native'

import Text from '@common/components/Text'
import useTheme from '@common/hooks/useTheme'
import { TabType } from '@common/modules/dashboard/components/TabsAndSearch/Tabs/Tab/Tab'

import { RADII } from '../tokens'
import { ChevronDownIcon } from './HomeIcons'

// The dashboard's scrolling column is a RN-Web ScrollView that carries a CSS
// `transform` — and a transformed ancestor becomes the containing block for
// `position: fixed` descendants, trapping the menu inside the card (painted under
// the right column). Portaling the menu to <body> escapes that ancestor so the
// fixed coords are truly viewport-relative. Web-only; native keeps the inline menu.
const createPortal: ((node: React.ReactNode, container: Element) => React.ReactNode) | null =
  Platform.OS === 'web' ? require('react-dom').createPortal : null
const toBody = (node: React.ReactNode): React.ReactNode =>
  createPortal && typeof document !== 'undefined' ? createPortal(node, document.body) : node

const OPTIONS: { tab: TabType; label: string }[] = [
  { tab: 'tokens', label: 'Tokens' },
  { tab: 'collectibles', label: 'NFTs' },
  { tab: 'defi', label: 'DeFi' }
]

type Props = {
  value: TabType
  onChange: (tab: TabType) => void
  /** Shrink height/padding/text for tight mobile toolbars (matches NetworkFilterDropdown). */
  compact?: boolean
}

const AssetTypeDropdown: React.FC<Props> = ({ value, onChange, compact }) => {
  const { theme } = useTheme()
  const triggerHeight = compact ? 30 : 36
  const triggerPadH = compact ? 8 : 16
  const fontSize = compact ? 12 : 14
  const [open, setOpen] = useState(false)
  // Viewport-anchored menu position. The menu is rendered `position: fixed` so it
  // escapes the asset card's ScrollView stacking context — an `absolute` menu was
  // painted UNDER the dashboard's right column (a later DOM sibling).
  const [anchor, setAnchor] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const triggerRef = useRef<View>(null)
  const current = OPTIONS.find((o) => o.tab === value) || OPTIONS[0]

  const toggle = useCallback(() => {
    if (open) {
      setOpen(false)
      return
    }
    // measureInWindow gives viewport coords for the fixed-position menu.
    triggerRef.current?.measureInWindow((x, y, _w, h) => {
      setAnchor({ top: y + h + 6, left: x })
      setOpen(true)
    })
  }, [open])

  return (
    <View style={{ position: 'relative', zIndex: 20 }}>
      <Pressable
        ref={triggerRef}
        onPress={toggle}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: compact ? 6 : 8,
          height: triggerHeight,
          paddingHorizontal: triggerPadH,
          borderRadius: RADII.pill,
          backgroundColor: theme.homeControlBackground as string,
          borderWidth: 1,
          borderColor: (open ? theme.primary : theme.homeCardBorder) as string
        }}
      >
        <Text fontSize={fontSize} weight="semiBold">
          {current.label}
        </Text>
        <ChevronDownIcon size={compact ? 14 : 16} color={theme.secondaryText as string} />
      </Pressable>

      {open && toBody(
        <>
          {/* outside-press catcher — frosted backdrop so the page dims/blurs behind the menu */}
          <Pressable
            onPress={() => setOpen(false)}
            style={
              {
                position: 'fixed',
                inset: 0,
                zIndex: 9998,
                backgroundColor: 'rgba(0,0,0,0.2)',
                backdropFilter: 'blur(3px)'
              } as any
            }
          />
          <View
            style={
              {
                position: 'fixed',
                top: anchor.top,
                left: anchor.left,
                minWidth: 160,
                zIndex: 9999,
                backgroundColor: theme.homeCardBackground as string,
                borderWidth: 1,
                borderColor: theme.homeCardBorder as string,
                borderRadius: RADII.md,
                paddingVertical: 4,
                boxShadow: '0 12px 32px rgba(20,24,51,0.12)'
              } as any
            }
          >
            {OPTIONS.map((opt) => {
              const active = opt.tab === value
              return (
                <Pressable
                  key={opt.tab}
                  onPress={() => {
                    onChange(opt.tab)
                    setOpen(false)
                  }}
                  style={{
                    paddingVertical: 10,
                    paddingHorizontal: 16,
                    backgroundColor: active ? (theme.quaternaryBackground as string) : 'transparent'
                  }}
                >
                  <Text fontSize={14} weight={active ? 'semiBold' : 'regular'}>
                    {opt.label}
                  </Text>
                </Pressable>
              )
            })}
          </View>
        </>
      )}
    </View>
  )
}

export default React.memo(AssetTypeDropdown)
